import { FC, ReactElement, useEffect, useState } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate } from 'react-router-dom';
import { Capacitor } from '@capacitor/core';
import { yupResolver } from '@hookform/resolvers/yup';
import { Stack, Typography } from '@mui/material';

import { personalDetailsManager } from '@mopla/business-logic';
import { PersonalDetailsFields } from '@mopla/constants';
import { IWhoami } from '@mopla/data-models';
import { useLogger } from '@mopla/logger';
import { getSubmitDisabled } from '@mopla/utils';

import { MplButton, MplErrorModal } from '../../';
import { FormTitle } from '../../layouts/WelcomeScreen';

import { FormFieldsV2 } from './FormFieldsV2';
import { FormFooterV2 } from './FormFooterV2';
import { JobticketDetailsForm } from './JobticketDetailsForm';
import {
	Form,
	FormWrapper,
	SubtitleWrapper,
} from './PersonalDetailsForm.styles';
import { PersonalDetailsValidationSchema } from './PersonalDetailsValidation';

export interface IUserPersonalDetails {
	firstName: string;
	lastName: string;
	phone: string;
	dateOfBirth: string | null;
	street: string | null;
	streetNumber: string | null;
	zipcode: string | null;
	city: string | null;
}

type TRenderFn = (parts: {
	content: ReactElement;
	footer: ReactElement;
}) => ReactElement;

type TTitleType =
	| 'alreadyVerified'
	| 'notSameDevice'
	| 'sameDeviceTitle1'
	| 'sameDevice';

export interface IPersonalDetailsPropsV2 {
	onEdit: (data: IUserPersonalDetails) => Promise<any>;
	onUserEdited?: () => void;
	currentUser?: IWhoami;
	isUserProfile?: boolean;
	onAbort?: (isDirty: boolean) => void;
	render?: TRenderFn;
}

interface IFormInput {
	[PersonalDetailsFields.firstName]: string;
	[PersonalDetailsFields.surname]: string;
	[PersonalDetailsFields.phoneNumber]: string;
	[PersonalDetailsFields.street]: string;
	[PersonalDetailsFields.streetNumber]: string;
	[PersonalDetailsFields.postCode]: string;
	[PersonalDetailsFields.location]: string;
	[PersonalDetailsFields.day]: string;
	[PersonalDetailsFields.month]: string;
	[PersonalDetailsFields.year]: string;
}

const defaultRenderFn: TRenderFn = ({ content, footer }) => {
	return (
		<>
			{content}
			{footer}
		</>
	);
};

export const PersonalDetailsFormV2: FC<IPersonalDetailsPropsV2> = ({
	onEdit,
	onUserEdited,
	currentUser,
	isUserProfile = false,
	onAbort,
	render = defaultRenderFn,
}) => {
	const { t } = useTranslation([
		'personalDetails',
		'validation',
		'profile',
		'errors',
	]);
	const navigate = useNavigate();
	const location = useLocation();
	const logger = useLogger('PersonalDetailsFormV2');

	const [titleType, setTitleType] = useState<TTitleType | null>(
		'notSameDevice'
	);
	const [showErrorModal, setShowErrorModal] = useState(false);

	useEffect(() => {
		const { title = null } = (location.state as Record<string, string>) || {};
		setTitleType(title as TTitleType | null);
	}, [location.state]);

	const methods = useForm<IFormInput>({
		defaultValues: {
			[PersonalDetailsFields.firstName]: currentUser?.firstName || '',
			[PersonalDetailsFields.surname]: currentUser?.lastName || '',
			[PersonalDetailsFields.phoneNumber]: currentUser?.phone || '',
			[PersonalDetailsFields.street]: currentUser?.address?.street || '',
			[PersonalDetailsFields.streetNumber]:
				currentUser?.address?.streetNumber || '',
			[PersonalDetailsFields.postCode]: currentUser?.address?.zipCode || '',
			[PersonalDetailsFields.location]: currentUser?.address?.city || '',
			[PersonalDetailsFields.day]: currentUser?.dateOfBirth
				? new Date(currentUser.dateOfBirth).getDate().toLocaleString('en-US', {
						minimumIntegerDigits: 2,
						useGrouping: false,
				  })
				: '',
			[PersonalDetailsFields.month]: currentUser?.dateOfBirth
				? (new Date(currentUser.dateOfBirth).getMonth() + 1).toLocaleString(
						'en-US',
						{
							minimumIntegerDigits: 2,
							useGrouping: false,
						}
				  )
				: '',
			[PersonalDetailsFields.year]: currentUser?.dateOfBirth
				? new Date(currentUser.dateOfBirth).getFullYear().toString()
				: '',
		},
		resolver: yupResolver(PersonalDetailsValidationSchema),
		mode: 'onTouched',
	});
	const { formState, handleSubmit } = methods;
	const { isDirty } = formState;

	const onSubmit = async (data: IFormInput) => {
		const {
			firstName,
			surname,
			phoneNumber,
			street,
			streetNumber,
			postCode,
			location,
			day,
			month,
			year,
		} = data;
		const personalDetails = {
			firstName: firstName.trim(),
			lastName: surname.trim(),
			phone: phoneNumber.trim(),
			dateOfBirth:
				year && month && day
					? new Date(
							`${year}-${month.padStart(2, '0')}-${day.padStart(2, '0')}`
					  ).toISOString()
					: null,
			street: street,
			streetNumber: streetNumber,
			zipcode: postCode.trim() || null,
			city: location.trim() || null,
		};

		try {
			await onEdit(personalDetails);
		} catch (error) {
			logger.error('Failed to submit personal details', error);
			setShowErrorModal(true);
			return;
		}

		if (isUserProfile && onUserEdited) {
			return onUserEdited();
		}
		personalDetailsManager.set('completed');

		return !Capacitor.isNativePlatform() &&
			titleType !== 'notSameDevice' &&
			!isUserProfile
			? navigate({ pathname: '/welcome', search: 'tryOurApp' })
			: navigate('/home');
	};

	const content = (
		<FormProvider {...methods}>
			<MplErrorModal
				open={showErrorModal}
				testId="PersonalDetailsFormV2-MplErrorModal"
				buttons={
					<MplButton fullWidth onClick={() => setShowErrorModal(false)}>
						{t('errors:button.close')}
					</MplButton>
				}
			/>
			<Form
				id="personalDetailsForm"
				onSubmit={handleSubmit((data) => onSubmit(data))}
			>
				<Stack className="content">
					<FormTitle>{t('text.header')}</FormTitle>

					<SubtitleWrapper>
						{titleType === 'alreadyVerified' && (
							<Typography
								variant="b2"
								color="solutions.600"
								data-testid="personal-details-title-alreadyVerified"
								mb={3}
							>
								{t('text.already_verified')}
							</Typography>
						)}
						{/*TODO needs to be investigated. Before the refactoring, 2 different titles were used.*/}
						{(titleType === 'notSameDevice' ||
							titleType === 'alreadyVerified' ||
							titleType === 'sameDeviceTitle1' ||
							titleType === 'sameDevice') && (
							<Typography
								variant="b2"
								color="solutions.600"
								data-testid="personal-details-title-moreInfo"
							>
								{t('text.title1')}
							</Typography>
						)}
					</SubtitleWrapper>

					<FormWrapper>
						{isUserProfile && <JobticketDetailsForm userData={currentUser} />}
						<FormFieldsV2
							withPaddings={isUserProfile}
							showAddressToggle={!isUserProfile}
						/>
					</FormWrapper>
				</Stack>
			</Form>
		</FormProvider>
	);

	const footer = (
		<FormFooterV2
			isSubmitDisabled={getSubmitDisabled(methods)}
			isUserProfile={isUserProfile}
			onCancel={() => {
				onAbort?.(isDirty);
			}}
		/>
	);

	return render({
		content,
		footer,
	});
};
